import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ExportButton, type ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import { trackAnalyticsExport } from '@/modules/analytics/utils/analyticsTracking';
import { buildExportFilename } from '@/modules/analytics/utils/exportFilename';
import { PageName } from '@/shared/types/segment';
import { downloadCsv, downloadJson } from '@/util/fileManager';

import { type AiAgentHallucinationRow } from './hooks/useAiAgentsHallucinations';

const buildRows = (data: AiAgentHallucinationRow[]) =>
  data.map((row) => ({
    ai_agent: row.name,
    total_messages: row.totalMessages,
    factual_hallucination: row.detectedFactual,
    jailbreak: row.detectedJailbreak,
    out_of_scope: row.detectedOutOfScope,
  }));

interface AiAgentsHallucinationExportProps {
  data: AiAgentHallucinationRow[];
  disabled?: boolean;
}

export const AiAgentsHallucinationExport = ({
  data,
  disabled,
}: AiAgentsHallucinationExportProps) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = useCallback(
    (format: ExportFormat) => {
      try {
        setIsExporting(true);
        trackAnalyticsExport(PageName.ANALYTICS_AI_AGENTS, 'hallucination_incidents', format);

        const rows = buildRows(data);
        const filename = buildExportFilename('hallucination_incidents');

        if (format === 'json') {
          downloadJson(rows, filename);
          return;
        }
        downloadCsv(rows, filename);
      } catch {
        toast.error(t('analytics.export_error'));
      } finally {
        setIsExporting(false);
      }
    },
    [data, t]
  );

  return (
    <ExportButton
      variant="icon"
      onExport={handleExport}
      isExporting={isExporting}
      disabled={disabled}
    />
  );
};
